import MedReqStatusBadge from '@/components/shared/MedReqStatusBadge'
import type { MedicationRequestItem } from '@/components/shared/PreviousMedicationRequestsList'
import { useMedReqCards, useMergedPatientMedReqCards } from '@/hooks/useMedReqCards'
import { formatDate } from '@/lib/utils'
import { borderRadius, colors, fontSizes, fontWeights } from '@/theme'
import type { CandidatePatient } from '@/types/deduplication'
import { ArrowForward, ExpandMore } from '@mui/icons-material'
import { Box, Chip, CircularProgress, Collapse, IconButton, Paper, Typography } from '@mui/material'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { gridColumns } from '../grid'

const SUBSCRIPTION_LABELS: Record<string, string> = {
  one_off: 'One-off',
  subscription: 'Subscription'
}

function formatSubscriptionLabel(subscription: string): string {
  return SUBSCRIPTION_LABELS[subscription] ?? subscription
}

function formatCardDate(date: string): string {
  try {
    return formatDate(new Date(date))
  } catch {
    return date
  }
}

// Roughly two full "tall" cards (status + type + name + prescribed item + order# + date + chip + footer)
// plus container padding & gap. Tuned by eye to leave a third card peeking when scrollable.
const SCROLL_MAX_HEIGHT = 600

const scrollableSx = {
  maxHeight: SCROLL_MAX_HEIGHT,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
  pr: 0.5,
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-thumb': { backgroundColor: colors.border.default, borderRadius: 3 }
} as const

const placeholderSx = {
  border: `1px dashed ${colors.border.default}`,
  borderRadius: borderRadius.md,
  bgcolor: colors.background.surface,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  px: 1.5,
  py: 2,
  minHeight: 120
} as const

function MedReqCard({ med, onClick }: { med: MedicationRequestItem; onClick: () => void }) {
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        flexShrink: 0,
        border: `1px solid ${colors.border.default}`,
        borderRadius: borderRadius.md,
        cursor: 'pointer',
        transition: 'all 0.2s',
        bgcolor: colors.background.surface,
        overflow: 'hidden',
        '&:hover': { borderColor: colors.border.hover, boxShadow: 1 }
      }}
    >
      <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
        <MedReqStatusBadge status={med.status} />
      </Box>
      <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {med.type && (
          <Typography
            sx={{
              fontSize: fontSizes.xs,
              color: colors.text.secondary,
              textTransform: 'uppercase',
              letterSpacing: '0.06em'
            }}
          >
            {med.type}
          </Typography>
        )}
        <Typography
          sx={{
            fontSize: fontSizes.base,
            fontWeight: fontWeights.bold,
            color: colors.text.dark,
            overflowWrap: 'anywhere'
          }}
        >
          {med.name || 'Unnamed medication'}
        </Typography>
        {med.prescribedItem && (
          <Box
            sx={{
              mt: 0.5,
              px: 1,
              py: 0.75,
              borderRadius: borderRadius.xs,
              bgcolor: colors.brand.primaryLight,
              borderLeft: `3px solid ${colors.brand.primary}`
            }}
          >
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: fontWeights.bold,
                color: colors.brand.primary,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                lineHeight: 1
              }}
            >
              Prescribed Item
            </Typography>
            <Typography
              sx={{
                fontSize: fontSizes.sm,
                fontWeight: fontWeights.semibold,
                color: colors.text.dark,
                overflowWrap: 'anywhere',
                lineHeight: 1.35
              }}
            >
              {med.prescribedItem}
            </Typography>
          </Box>
        )}
        {med.orderNumber && (
          <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.secondary }}>
            Order #{med.orderNumber}
          </Typography>
        )}
        {med.date && (
          <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.secondary }}>
            {formatCardDate(med.date)}
          </Typography>
        )}
        {med.subscription && (
          <Box sx={{ mt: 0.5 }}>
            <Chip label={formatSubscriptionLabel(med.subscription)} size='small' variant='neutral' />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1.5,
          py: 0.75,
          bgcolor: colors.brand.primaryLight
        }}
      >
        <Typography
          sx={{
            fontSize: fontSizes.xs,
            color: colors.brand.primary,
            fontWeight: fontWeights.semibold,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}
        >
          View request
          <ArrowForward sx={{ fontSize: '12px' }} />
        </Typography>
      </Box>
    </Paper>
  )
}

function CountHeader({
  count,
  isLoading,
  expanded,
  onToggle
}: {
  count: number
  isLoading: boolean
  expanded: boolean
  onToggle?: () => void
}) {
  const label = isLoading ? 'Loading…' : `${count} request${count !== 1 ? 's' : ''}`
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, pr: 0.25 }}>
      <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.secondary }}>{label}</Typography>
      {onToggle && (
        <IconButton
          size='small'
          onClick={onToggle}
          aria-label={expanded ? 'Collapse requests' : 'Expand requests'}
          sx={{
            p: 0.25,
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }}
        >
          <ExpandMore fontSize='small' />
        </IconButton>
      )}
    </Box>
  )
}

function MedReqColumnView({
  isLoading,
  medicationRequests,
  onCardClick
}: {
  isLoading: boolean
  medicationRequests: MedicationRequestItem[]
  onCardClick: (id: string) => void
}) {
  const [expanded, setExpanded] = useState(true)
  const count = medicationRequests.length
  const hasItems = count > 0

  return (
    <Box>
      <CountHeader
        count={count}
        isLoading={isLoading}
        expanded={expanded}
        onToggle={hasItems ? () => setExpanded(e => !e) : undefined}
      />
      <Collapse in={expanded}>
        {isLoading ? (
          <Box sx={placeholderSx}>
            <CircularProgress size={20} />
          </Box>
        ) : !hasItems ? (
          <Box sx={placeholderSx}>
            <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.secondary }}>
              No medication requests
            </Typography>
          </Box>
        ) : (
          <Box sx={scrollableSx}>
            {medicationRequests.map(med => (
              <MedReqCard key={med.id} med={med} onClick={() => onCardClick(String(med.id))} />
            ))}
          </Box>
        )}
      </Collapse>
    </Box>
  )
}

function PatientMedReqColumn({ patient, backLabel }: { patient: CandidatePatient; backLabel: string }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { medicationRequests, isLoading } = useMedReqCards({ patientHash: patient.hash })

  const handleCardClick = (medId: string) => {
    navigate(`/med-requests/${medId}`, {
      state: { backLabel, fallbackPath: location.pathname }
    })
  }

  return (
    <MedReqColumnView isLoading={isLoading} medicationRequests={medicationRequests} onCardClick={handleCardClick} />
  )
}

function MergedMedReqColumn({ patients, backLabel }: { patients: CandidatePatient[]; backLabel: string }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { medicationRequests, isLoading } = useMergedPatientMedReqCards(patients.map(p => p.hash))

  const handleCardClick = (medId: string) => {
    navigate(`/med-requests/${medId}`, {
      state: { backLabel, fallbackPath: location.pathname }
    })
  }

  return (
    <MedReqColumnView isLoading={isLoading} medicationRequests={medicationRequests} onCardClick={handleCardClick} />
  )
}

interface MedicationRequestsCardsRowProps {
  patients: CandidatePatient[]
  showMergedColumn?: boolean
  backLabel?: string
}

export default function MedicationRequestsCardsRow({
  patients,
  showMergedColumn = false,
  backLabel = 'Back to review'
}: MedicationRequestsCardsRowProps) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: gridColumns(patients.length, showMergedColumn),
        alignItems: 'flex-start',
        mt: 1
      }}
    >
      <Typography
        sx={{ fontSize: fontSizes.sm, color: colors.brand.primary, fontWeight: fontWeights.bold, pl: 3, py: 1.5 }}
      >
        Medication requests
      </Typography>
      {patients.map(patient => (
        <Box key={patient.hash} sx={{ px: 1.5, py: 1.5 }}>
          <PatientMedReqColumn patient={patient} backLabel={backLabel} />
        </Box>
      ))}
      {showMergedColumn && (
        <Box sx={{ px: 1.5, py: 1.5, bgcolor: colors.grey[50] }}>
          <MergedMedReqColumn patients={patients} backLabel={backLabel} />
        </Box>
      )}
    </Box>
  )
}
